import { useState, useEffect } from 'react';
import {
  startLoginFlow,
  isAuthenticated,
  getUserToken,
  setUserToken,
  logout,
  getMaskedToken,
} from '../services/auth';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Link, Unlink, Loader2, CheckCircle, Key } from 'lucide-react';

const POLLINATIONS_APP_KEY = 'pk_U4qmr40QK09qCPzn';

interface PollinationsConnectProps {
  onConnectionChange?: (connected: boolean) => void;
  showStatus?: boolean;
  compact?: boolean;
}

export const PollinationsConnect: React.FC<PollinationsConnectProps> = ({
  onConnectionChange,
  showStatus = true,
  compact = false,
}) => {
  const [connected, setConnected] = useState(isAuthenticated());
  const [loading, setLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualKey, setManualKey] = useState('');
  const token = connected ? getUserToken() : null;

  useEffect(() => {
    onConnectionChange?.(connected);
  }, [connected, onConnectionChange]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      await startLoginFlow(POLLINATIONS_APP_KEY);
    } catch {
      setLoading(false);
    }
  };

  const handleManualSave = () => {
    if (manualKey.trim().startsWith('sk_')) {
      setUserToken(manualKey.trim());
      setConnected(true);
      setShowManualEntry(false);
      setManualKey('');
    }
  };

  const handleDisconnect = () => {
    logout();
    setConnected(false);
  };

  if (connected && token) {
    return (
      <div className={`flex items-center ${compact ? 'gap-2' : 'justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800'}`}>
        {showStatus && (
          <div className={compact ? 'flex items-center gap-2 text-sm text-green-700 dark:text-green-300' : ''}>
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            {!compact && (
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">Connected to Pollinations</p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  Using your Pollen credits ({getMaskedToken(token)})
                </p>
              </div>
            )}
            {compact && <span>Connected</span>}
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDisconnect}
          leftIcon={<Unlink className="w-4 h-4" />}
          className={compact ? 'text-red-600 hover:text-red-700' : ''}
        >
          Disconnect
        </Button>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'space-y-4'}>
      {!compact && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                Connect Your Pollinations Account
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Use your own Pollen credits for AI-powered flashcard generation
              </p>
            </div>
          </div>
        </div>
      )}

      {showManualEntry ? (
        <div className="space-y-3">
          <Input
            label="Pollinations API Key"
            type="password"
            value={manualKey}
            onChange={(e) => setManualKey(e.target.value)}
            placeholder="sk_..."
          />
          <div className="flex gap-2">
            <Button onClick={handleManualSave} disabled={!manualKey.trim().startsWith('sk_')} size="sm">
              Save Key
            </Button>
            <Button variant="ghost" onClick={() => setShowManualEntry(false)} size="sm">
              Cancel
            </Button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Get your API key from <a href="https://enter.pollinations.ai" target="_blank" rel="noopener noreferrer" className="underline">enter.pollinations.ai</a>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            onClick={handleConnect}
            disabled={loading}
            leftIcon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
            variant={compact ? 'outline' : 'primary'}
            size={compact ? 'sm' : 'md'}
            className={compact ? '' : 'w-full'}
          >
            {loading ? 'Connecting...' : 'Connect Pollinations Account'}
          </Button>
          <Button
            onClick={() => setShowManualEntry(true)}
            variant="ghost"
            size="sm"
            leftIcon={<Key className="w-4 h-4" />}
            className={compact ? '' : 'w-full'}
          >
            Enter API Key Manually
          </Button>
        </div>
      )}

      {!compact && !showManualEntry && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          You'll be redirected to Pollinations to authorize. Your API key stays in your browser.
        </p>
      )}
    </div>
  );
};

export default PollinationsConnect;
